<template>
    <section
        ref="section"
        :data-mui-theme="theme"
        :class="['slice  slice-testimonials--default', {'slice-testimonials--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="testimonials"
        data-testid="testimonials-slice"
    >
        <div class="slice-content slice-testimonials--default__content mui-background-lighter">
            <div class="slice-testimonials--default__header">
                <PrismicRichText
                    v-if="isFilled.richText(slice.primary.title)"
                    :field="slice.primary.title"
                    class="slice-title mui-title"
                    data-testid="testimonials-default-title"
                />
                <TestimonialsDefaultSliderControls
                    v-if="showCarouselControls"
                    class="slice-testimonials--default__controls"
                    :navigation-prev-class="navigationPrevClass"
                    :navigation-next-class="navigationNextClass"
                    :next-arrow-label="slice.primary.nextArrowLabel"
                    :prev-arrow-label="slice.primary.prevArrowLabel"
                />
            </div>

            <div class="slice-testimonials--default__swiper-wrapper">
                <NuxtErrorBoundary>
                    <Swiper
                        :modules="[Controller, Navigation]"
                        :slides-per-view="'auto'"
                        :space-between="16"
                        :navigation="{nextEl: `.${navigationNextClass}`, prevEl: `.${navigationPrevClass}`}"
                        class="slice-testimonials--default__swiper"
                        @swiper="onSwiper"
                        @resize="onResize"
                    >
                        <SwiperSlide v-for="(item, i) in slice.items" :key="`testimonials-desktop-${i}`">
                            <TestimonialsDefaultCard :item="item" :index="i" />
                        </SwiperSlide>
                    </Swiper>
                </NuxtErrorBoundary>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, onMounted, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSlice} from '@prismicio-types';
    // @ts-expect-error No declaration file found for module 'swiper'.
    import {Controller, Navigation} from 'swiper';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import 'swiper/css';
    import {useComponentTracking} from '#imports';
    import useErrorHelpers from '../../../composables/useErrorHelpers';
    import TestimonialsDefaultCard from '../components/TestimonialsDefaultCard.vue';
    import TestimonialsDefaultSliderControls from '../components/TestimonialsDefaultSliderControls.vue';
    import {useUniqueSelectors, useSwiperNavigation} from '../composables/useSwiperNavigation';

    type TestimonialsDefaultInZone = Extract<TestimonialsSlice, {variation: 'default'}>;

    const props = defineProps(getSliceComponentProps<TestimonialsDefaultInZone>());

    const section = useTemplateRef('section');

    const theme = computed(() => props.slice.primary.theme);

    const showCarouselControls = computed(() => props.slice.items.length > 3);

    const {navigationPrevClass, navigationNextClass} = useUniqueSelectors('default');
    const {onSwiper, onResize} = useSwiperNavigation();

    onMounted(() => {
        const {getPreviewErrorHandler, throwIfMissingItemsProperties} = useErrorHelpers();
        const errorHandler = getPreviewErrorHandler();

        throwIfMissingItemsProperties(
            errorHandler.toast,
            props.slice.items,
            [
                {name: 'testimonialIllustration', type: 'image'},
                {name: 'testimonialDescription', type: 'field'},
                {name: 'testimonialVideo', type: 'video'},
                {name: 'testimonialsVideoCover', type: 'image'},
                {name: 'testimonialLogo', type: 'image'},
                {name: 'intervieweeName', type: 'rawText'},
                {name: 'intervieweeJobTitle', type: 'rawText'},
                {name: 'link', type: 'link'},
            ],
            'testimonial',
        );
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-testimonials {
        &--hide-in-mobile {
            @include mq.screen_md(min) {
                display: none;
            }
        }

        &--default {
            @include mq.screen_md(min) {
                padding: 0 var(--joy-core-spacing-6);
            }

            .slice-content {
                text-align: left;

                @include mq.screen_md(min) {
                    margin: var(--joy-core-spacing-6) auto;
                    border-radius: var(--joy-core-spacing-6);
                }
            }

            &__content {
                text-align: left;
                overflow: hidden;
            }

            &__header {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: flex-end;
                gap: var(--joy-core-spacing-8);
                margin-bottom: var(--joy-core-spacing-6);

                @include mq.screen_md(min) {
                    margin-bottom: var(--joy-core-spacing-12);
                }
            }

            &__controls {
                display: none;

                @include mq.screen_sm(min) {
                    display: flex;
                    justify-content: flex-end;
                    gap: var(--joy-core-spacing-2);
                }
            }

            &__swiper-wrapper {
                display: flex;
                margin-right: calc(-1 * var(--joy-core-spacing-6));

                .swiper {
                    opacity: 0;
                    transition: opacity var(--joy-transition-duration-default);

                    &.swiper-initialized {
                        opacity: 1;
                    }
                }

                .swiper-slide {
                    width: 280px;
                    height: auto;
                    padding: var(--joy-core-spacing-2) 0 var(--joy-core-spacing-7);

                    @include mq.screen_md(min) {
                        width: 394px;
                    }

                    > div {
                        > div {
                            > div {
                                height: 100%;
                            }
                        }
                    }
                }
            }
        }
    }
</style>
